import { Image, Link, Page, Text, View } from '@react-pdf/renderer';
import React from 'react';
import { Header } from './Header';
import { getPdfLabelStyle, pdfStyles } from '../pdfStyles';
import { Footer } from './Footer';
import { NoData } from './NoData';
import { TitleLink } from './TitleLink';
import { ScenariosTable } from './ScenariosTable';
import { formatDate, formatNumber } from 'src/utils/misc';
import { formatUrl } from 'src/utils/misc';
import { extLinkImg } from '../imgData';
import { getExternalPath } from '../util';
import { ColorSet } from 'src/redux/reducers/theme';
import { isNil } from 'lodash';
import { ActionsConf, ControlPDF, ScenarioTablePDF } from '../types';
import { PdfLinkifyText } from './PdfLinkifyText';
import { ActionsTable } from './ActionsTable';

interface RmpPageProps {
  control: ControlPDF;
  scenarios?: ScenarioTablePDF[];
  onRender: (pageNumber: number) => void;
  endComponent?: React.ReactNode;
  footerComponent?: React.ReactNode;
  colors: ColorSet;
  includesActions: ActionsConf;
  logoSrc?: string;
  secondaryColor?: string | null;
}

export const RmpPage: React.FC<RmpPageProps> = ({
  control,
  scenarios,
  onRender,
  endComponent,
  footerComponent,
  colors,
  includesActions,
  logoSrc,
  secondaryColor,
}) => {
  const labelStyle = getPdfLabelStyle(secondaryColor);

  const relatedScenarios = scenarios || control.riskManagementPlans?.map((rmp) => rmp.scenario).filter(Boolean) || [];

  const sourceFramework = control.frameworkControls[0];

  return (
    <Page size={'A4'} style={pdfStyles.page}>
      <Header endComponent={endComponent} colors={colors} logoSrc={logoSrc} />

      {/* @ts-ignore */}
      <View render={(data) => onRender(data.pageNumber)} />
      <View style={pdfStyles.section}>
        <TitleLink
          title={control.name}
          intLinkId={control.name.toLowerCase()}
          extLink={`risk-management/control/${control.id}`}
          colors={colors}
        />

        <View style={pdfStyles.spacer8} />
        <View style={pdfStyles.spacer4} />
        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>STRATEGY</Text>
            {control.strategy ? <Text style={pdfStyles.value}>{control.strategy}</Text> : <NoData colors={colors} />}
          </View>

          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>STATUS</Text>
            <View style={pdfStyles.spacer1} />
            {control.status ? <Text style={pdfStyles.value}>{control.status}</Text> : <NoData colors={colors} />}
          </View>
          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>LINK</Text>
            <View style={pdfStyles.spacer1} />
            {control.linkUrl && control.linkText ? (
              <Link
                src={formatUrl(control.linkUrl)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
              >
                <Text
                  style={{
                    ...pdfStyles.value,
                    textDecoration: 'underline',
                    marginRight: 6,
                  }}
                >
                  {control.linkText}
                </Text>
                {/* <Image src={extLinkImg} style={{ width: 8, height: 8 }} /> */}
              </Link>
            ) : control.linkUrl ? (
              <Link
                src={formatUrl(control.linkUrl)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
              >
                <Text
                  style={{
                    ...pdfStyles.value,
                    textDecoration: 'underline',
                    color: 'blue',
                    marginRight: 6,
                  }}
                >
                  {control.linkUrl}
                </Text>
                {/* <Image src={extLinkImg} style={{ width: 8, height: 8 }} /> */}
              </Link>
            ) : control.linkText ? (
              <Text style={pdfStyles.value}>{control.linkText}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
        </View>

        <View style={pdfStyles.spacer8} />

        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>OWNER</Text>
            {control.owner ? (
              <Text style={pdfStyles.value}>{(control.owner.text || control.owner.email || '-').split('')}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>ANNUAL COST</Text>
            <View style={pdfStyles.spacer1} />
            {control.annualCost ? (
              <Text style={pdfStyles.value}>{formatNumber(control.annualCost, { symbol: true })}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={labelStyle}>VENDOR</Text>
            <View style={pdfStyles.spacer1} />
            {control.vendor ? (
              <PdfLinkifyText style={pdfStyles.value}>{control.vendor}</PdfLinkifyText>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
        </View>

        <View style={pdfStyles.spacer8} />

        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>EXISTING MATURITY</Text>
            <View style={pdfStyles.spacer1} />
            {!isNil(control.existingMaturity) ? (
              <Text style={pdfStyles.value}>{control.existingMaturity}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>

          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>TARGET MATURITY</Text>
            <View style={pdfStyles.spacer1} />
            {!isNil(control.targetMaturity) ? (
              <Text style={pdfStyles.value}>{control.targetMaturity}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
        </View>

        <View style={pdfStyles.spacer8} />

        <View style={{ flexDirection: 'row' }}>
          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>EXPECTED LIVE DATE</Text>
            <View style={pdfStyles.spacer1} />
            {control.expectedActiveDate ? (
              <Text style={pdfStyles.value}>{formatDate(control.expectedActiveDate, { emptyValue: '-' })}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>ACTUAL LIVE DATE</Text>
            <View style={pdfStyles.spacer1} />
            {control.actualActiveDate ? (
              <Text style={pdfStyles.value}>{formatDate(control.actualActiveDate, { emptyValue: '-' })}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
          <View style={{ width: 120, marginRight: 20 }}>
            <Text style={labelStyle}>REVIEW DATE</Text>
            <View style={pdfStyles.spacer1} />
            {control.reviewDate ? (
              <Text style={pdfStyles.value}>{formatDate(control.reviewDate, { emptyValue: '-' })}</Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
          <View style={{ marginRight: 20 }}>
            <Text style={labelStyle}>LAST EDITED</Text>
            <View style={pdfStyles.spacer1} />
            {control.meta?.updatedAt ? (
              <Text style={pdfStyles.value}>
                {formatDate(control.meta.updatedAt, { emptyValue: '-', formatType: 'datetime' })}
              </Text>
            ) : (
              <NoData colors={colors} />
            )}
          </View>
        </View>
        <View style={pdfStyles.spacer8} />

        <Text style={labelStyle}>DESCRIPTION</Text>
        {control.description ? <PdfLinkifyText>{control.description}</PdfLinkifyText> : <NoData colors={colors} />}
        <View style={pdfStyles.spacer8} />

        <Text style={labelStyle}>SOURCE FRAMEWORK</Text>
        <View style={pdfStyles.spacer4} />
        {sourceFramework ? (
          <View style={{ flexDirection: 'row' }}>
            <View style={{ width: 120, marginRight: 20 }}>
              <Text style={labelStyle}>CONTROL ID</Text>
              <View style={pdfStyles.spacer1} />
              <Link
                src={getExternalPath(`control-frameworks/all-controls/${sourceFramework.id}`)}
                style={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  textDecoration: 'none',
                }}
              >
                <Text style={{ ...pdfStyles.value, textDecoration: 'underline', marginRight: 6 }}>
                  {sourceFramework.control_identifier}
                </Text>
                {/* <Image src={extLinkImg} style={{ width: 8, height: 8 }} /> */}
              </Link>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={labelStyle}>FRAMEWORK</Text>
              <View style={pdfStyles.spacer1} />
              <Link
                src={getExternalPath(`control-frameworks/${sourceFramework.controlFramework?.id}`)}
                style={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  textDecoration: 'none',
                }}
              >
                <Text style={{ ...pdfStyles.value, textDecoration: 'underline', marginRight: 6 }}>
                  {sourceFramework.controlFramework?.shortname}
                </Text>
                {/* <Image src={extLinkImg} style={{ width: 8, height: 8 }} /> */}
              </Link>
            </View>
          </View>
        ) : (
          <NoData colors={colors} />
        )}

        <View style={pdfStyles.spacer8} />
        <View style={pdfStyles.spacer8} />

        {relatedScenarios.length > 0 ? (
          <ScenariosTable
            data={relatedScenarios.filter((s): s is NonNullable<typeof s> => Boolean(s))}
            colors={colors}
            riskManagementPlans={control.riskManagementPlans}
            secondaryColor={secondaryColor}
          />
        ) : null}

        <ActionsTable
          data={control.actions}
          colors={colors}
          includesActions={includesActions}
          secondaryColor={secondaryColor}
        />
      </View>
      <Footer footerComponent={footerComponent} colors={colors} />
    </Page>
  );
};
